import React, { useRef, useState, useEffect, useCallback } from 'react';
import { X, Check, Undo2, Square, Circle, ArrowRight, Pencil, Eraser } from 'lucide-react';
import clsx from 'clsx';

type Tool = 'rectangle' | 'circle' | 'arrow' | 'freehand' | 'eraser';

interface Point {
  x: number;
  y: number;
}

interface DrawingAction {
  tool: Tool;
  color: string;
  lineWidth: number;
  points: Point[];
  startPoint?: Point;
  endPoint?: Point;
}

interface ScreenshotEditorProps {
  imageDataUrl: string;
  onSave: (editedImageDataUrl: string) => void;
  onCancel: () => void;
}

export const ScreenshotEditor: React.FC<ScreenshotEditorProps> = ({
  imageDataUrl,
  onSave,
  onCancel
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [ctx, setCtx] = useState<CanvasRenderingContext2D | null>(null);
  const [backgroundImage, setBackgroundImage] = useState<HTMLImageElement | null>(null);

  const [tool, setTool] = useState<Tool>('rectangle');
  const [color, setColor] = useState('#ef4444'); // Rot als Standard
  const [lineWidth, setLineWidth] = useState(3);

  const [isDrawing, setIsDrawing] = useState(false);
  const [startPoint, setStartPoint] = useState<Point | null>(null);
  const [currentPoints, setCurrentPoints] = useState<Point[]>([]);
  const [actions, setActions] = useState<DrawingAction[]>([]);

  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const [scale, setScale] = useState(1);

  // Bild laden und Canvas initialisieren
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      setBackgroundImage(img);

      // Canvas-Größe basierend auf verfügbarem Platz berechnen
      const container = containerRef.current;
      if (container) {
        const maxWidth = container.clientWidth - 32; // Padding
        const maxHeight = container.clientHeight - 32;

        let width = img.width;
        let height = img.height;
        let newScale = 1;

        // Skalieren wenn nötig
        if (width > maxWidth || height > maxHeight) {
          const scaleX = maxWidth / width;
          const scaleY = maxHeight / height;
          newScale = Math.min(scaleX, scaleY);
          width = Math.floor(width * newScale);
          height = Math.floor(height * newScale);
        }

        setScale(newScale);
        setCanvasSize({ width, height });
      }
    };
    img.src = imageDataUrl;
  }, [imageDataUrl]);

  // Canvas-Kontext initialisieren
  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas && canvasSize.width > 0) {
      const context = canvas.getContext('2d');
      if (context) {
        setCtx(context);
      }
    }
  }, [canvasSize]);

  // Canvas neu zeichnen wenn sich etwas ändert
  const redrawCanvas = useCallback(() => {
    if (!ctx || !backgroundImage || canvasSize.width === 0) return;

    // Canvas leeren und Hintergrundbild zeichnen
    ctx.clearRect(0, 0, canvasSize.width, canvasSize.height);
    ctx.drawImage(backgroundImage, 0, 0, canvasSize.width, canvasSize.height);

    // Alle gespeicherten Aktionen zeichnen
    actions.forEach(action => {
      drawAction(ctx, action);
    });
  }, [ctx, backgroundImage, canvasSize, actions]);

  useEffect(() => {
    redrawCanvas();
  }, [redrawCanvas]);

  // Eine Aktion zeichnen
  const drawAction = (context: CanvasRenderingContext2D, action: DrawingAction) => {
    context.strokeStyle = action.color;
    context.lineWidth = action.lineWidth;
    context.lineCap = 'round';
    context.lineJoin = 'round';

    switch (action.tool) {
      case 'rectangle':
        if (action.startPoint && action.endPoint) {
          const width = action.endPoint.x - action.startPoint.x;
          const height = action.endPoint.y - action.startPoint.y;
          context.strokeRect(action.startPoint.x, action.startPoint.y, width, height);
        }
        break;

      case 'circle':
        if (action.startPoint && action.endPoint) {
          // Mittelpunkt = startPoint, Radius = Distanz zum endPoint
          const radius = Math.sqrt(
            Math.pow(action.endPoint.x - action.startPoint.x, 2) +
            Math.pow(action.endPoint.y - action.startPoint.y, 2)
          );

          context.beginPath();
          context.arc(action.startPoint.x, action.startPoint.y, radius, 0, 2 * Math.PI);
          context.stroke();
        }
        break;

      case 'arrow':
        if (action.startPoint && action.endPoint) {
          const headLength = 15;
          const dx = action.endPoint.x - action.startPoint.x;
          const dy = action.endPoint.y - action.startPoint.y;
          const angle = Math.atan2(dy, dx);

          // Linie
          context.beginPath();
          context.moveTo(action.startPoint.x, action.startPoint.y);
          context.lineTo(action.endPoint.x, action.endPoint.y);
          context.stroke();

          // Pfeilspitze
          context.beginPath();
          context.moveTo(action.endPoint.x, action.endPoint.y);
          context.lineTo(
            action.endPoint.x - headLength * Math.cos(angle - Math.PI / 6),
            action.endPoint.y - headLength * Math.sin(angle - Math.PI / 6)
          );
          context.moveTo(action.endPoint.x, action.endPoint.y);
          context.lineTo(
            action.endPoint.x - headLength * Math.cos(angle + Math.PI / 6),
            action.endPoint.y - headLength * Math.sin(angle + Math.PI / 6)
          );
          context.stroke();
        }
        break;

      case 'freehand':
        if (action.points.length > 1) {
          context.beginPath();
          context.moveTo(action.points[0]!.x, action.points[0]!.y);
          for (let i = 1; i < action.points.length; i++) {
            context.lineTo(action.points[i]!.x, action.points[i]!.y);
          }
          context.stroke();
        }
        break;

      case 'eraser':
        // Radierer entfernt Aktionen - nichts zu zeichnen
        break;
    }
  };

  // Position relativ zum Canvas berechnen (Mouse + Touch)
  const getCanvasPoint = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>): Point => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    const clientX = 'touches' in e ? (e.touches[0]?.clientX ?? e.changedTouches[0]?.clientX ?? 0) : e.clientX;
    const clientY = 'touches' in e ? (e.touches[0]?.clientY ?? e.changedTouches[0]?.clientY ?? 0) : e.clientY;
    return {
      x: clientX - rect.left,
      y: clientY - rect.top
    };
  };

  // Prüft, ob ein Punkt eine Aktion berührt (für den Radiergummi)
  const isPointNearAction = (point: Point, action: DrawingAction, threshold: number = 15): boolean => {
    switch (action.tool) {
      case 'rectangle':
        if (action.startPoint && action.endPoint) {
          const minX = Math.min(action.startPoint.x, action.endPoint.x);
          const maxX = Math.max(action.startPoint.x, action.endPoint.x);
          const minY = Math.min(action.startPoint.y, action.endPoint.y);
          const maxY = Math.max(action.startPoint.y, action.endPoint.y);

          // Prüfe Nähe zu den 4 Kanten
          const nearTop = point.y >= minY - threshold && point.y <= minY + threshold && point.x >= minX - threshold && point.x <= maxX + threshold;
          const nearBottom = point.y >= maxY - threshold && point.y <= maxY + threshold && point.x >= minX - threshold && point.x <= maxX + threshold;
          const nearLeft = point.x >= minX - threshold && point.x <= minX + threshold && point.y >= minY - threshold && point.y <= maxY + threshold;
          const nearRight = point.x >= maxX - threshold && point.x <= maxX + threshold && point.y >= minY - threshold && point.y <= maxY + threshold;

          return nearTop || nearBottom || nearLeft || nearRight;
        }
        return false;

      case 'circle':
        if (action.startPoint && action.endPoint) {
          const radius = Math.sqrt(
            Math.pow(action.endPoint.x - action.startPoint.x, 2) +
            Math.pow(action.endPoint.y - action.startPoint.y, 2)
          );
          const distToCenter = Math.sqrt(
            Math.pow(point.x - action.startPoint.x, 2) +
            Math.pow(point.y - action.startPoint.y, 2)
          );
          // Prüfe Nähe zum Kreisrand
          return Math.abs(distToCenter - radius) <= threshold;
        }
        return false;

      case 'arrow':
        if (action.startPoint && action.endPoint) {
          // Distanz Punkt zur Linie
          const A = point.x - action.startPoint.x;
          const B = point.y - action.startPoint.y;
          const C = action.endPoint.x - action.startPoint.x;
          const D = action.endPoint.y - action.startPoint.y;

          const dot = A * C + B * D;
          const lenSq = C * C + D * D;
          let param = lenSq !== 0 ? dot / lenSq : -1;

          let xx, yy;
          if (param < 0) {
            xx = action.startPoint.x;
            yy = action.startPoint.y;
          } else if (param > 1) {
            xx = action.endPoint.x;
            yy = action.endPoint.y;
          } else {
            xx = action.startPoint.x + param * C;
            yy = action.startPoint.y + param * D;
          }

          const dist = Math.sqrt(Math.pow(point.x - xx, 2) + Math.pow(point.y - yy, 2));
          return dist <= threshold;
        }
        return false;

      case 'freehand':
        // Prüfe Nähe zu jedem Punkt der Freihandlinie
        for (const p of action.points) {
          const dist = Math.sqrt(Math.pow(point.x - p.x, 2) + Math.pow(point.y - p.y, 2));
          if (dist <= threshold) return true;
        }
        return false;

      default:
        return false;
    }
  };

  // Zeichnen starten
  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if ('touches' in e) e.preventDefault();
    const point = getCanvasPoint(e);
    setIsDrawing(true);
    setStartPoint(point);
    setCurrentPoints([point]);
  };

  // Zeichnen
  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if ('touches' in e) e.preventDefault();
    if (!isDrawing || !ctx || !startPoint) return;

    const point = getCanvasPoint(e);

    // Canvas neu zeichnen
    redrawCanvas();

    // Aktuelle Zeichnung als Vorschau
    ctx.strokeStyle = color;
    ctx.lineWidth = lineWidth;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    if (tool === 'eraser') {
      // Radiergummi: Entferne Aktionen, die berührt werden
      setActions(prev => prev.filter(action => !isPointNearAction(point, action)));
      return;
    }

    if (tool === 'freehand') {
      setCurrentPoints(prev => [...prev, point]);

      ctx.beginPath();
      ctx.moveTo(currentPoints[0]?.x || point.x, currentPoints[0]?.y || point.y);
      currentPoints.forEach(p => ctx.lineTo(p.x, p.y));
      ctx.lineTo(point.x, point.y);
      ctx.stroke();
    } else if (tool === 'rectangle') {
      const width = point.x - startPoint.x;
      const height = point.y - startPoint.y;
      ctx.strokeRect(startPoint.x, startPoint.y, width, height);
    } else if (tool === 'circle') {
      // Mittelpunkt = startPoint, Radius = Distanz zum Mauszeiger
      const radius = Math.sqrt(
        Math.pow(point.x - startPoint.x, 2) +
        Math.pow(point.y - startPoint.y, 2)
      );

      ctx.beginPath();
      ctx.arc(startPoint.x, startPoint.y, radius, 0, 2 * Math.PI);
      ctx.stroke();
    } else if (tool === 'arrow') {
      const headLength = 15;
      const dx = point.x - startPoint.x;
      const dy = point.y - startPoint.y;
      const angle = Math.atan2(dy, dx);

      ctx.beginPath();
      ctx.moveTo(startPoint.x, startPoint.y);
      ctx.lineTo(point.x, point.y);
      ctx.stroke();

      ctx.beginPath();
      ctx.moveTo(point.x, point.y);
      ctx.lineTo(
        point.x - headLength * Math.cos(angle - Math.PI / 6),
        point.y - headLength * Math.sin(angle - Math.PI / 6)
      );
      ctx.moveTo(point.x, point.y);
      ctx.lineTo(
        point.x - headLength * Math.cos(angle + Math.PI / 6),
        point.y - headLength * Math.sin(angle + Math.PI / 6)
      );
      ctx.stroke();
    }
  };

  // Zeichnen beenden
  const handleMouseUp = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing || !startPoint) return;

    const endPoint = getCanvasPoint(e);

    // Radiergummi speichert keine Aktion (entfernt nur bestehende)
    if (tool === 'eraser') {
      setIsDrawing(false);
      setStartPoint(null);
      setCurrentPoints([]);
      return;
    }

    // Aktion speichern
    const action: DrawingAction = {
      tool,
      color,
      lineWidth,
      points: tool === 'freehand' ? [...currentPoints, endPoint] : [],
      startPoint: tool !== 'freehand' ? startPoint : undefined,
      endPoint: tool !== 'freehand' ? endPoint : undefined
    };

    setActions(prev => [...prev, action]);
    setIsDrawing(false);
    setStartPoint(null);
    setCurrentPoints([]);
  };

  // Letzte Aktion rückgängig machen
  const handleUndo = () => {
    setActions(prev => prev.slice(0, -1));
  };

  // Speichern - Bild mit Zeichnungen exportieren
  const handleSave = () => {
    if (!backgroundImage) return;

    // Neues Canvas in Originalgröße erstellen
    const exportCanvas = document.createElement('canvas');
    exportCanvas.width = backgroundImage.width;
    exportCanvas.height = backgroundImage.height;
    const exportCtx = exportCanvas.getContext('2d');

    if (!exportCtx) return;

    // Hintergrundbild in Originalgröße zeichnen
    exportCtx.drawImage(backgroundImage, 0, 0);

    // Skalierungsfaktor für Zeichnungen
    const exportScale = backgroundImage.width / canvasSize.width;

    // Alle Aktionen in Originalgröße zeichnen
    actions.forEach(action => {
      exportCtx.strokeStyle = action.color;
      exportCtx.lineWidth = action.lineWidth * exportScale;
      exportCtx.lineCap = 'round';
      exportCtx.lineJoin = 'round';

      switch (action.tool) {
        case 'rectangle':
          if (action.startPoint && action.endPoint) {
            const x = action.startPoint.x * exportScale;
            const y = action.startPoint.y * exportScale;
            const width = (action.endPoint.x - action.startPoint.x) * exportScale;
            const height = (action.endPoint.y - action.startPoint.y) * exportScale;
            exportCtx.strokeRect(x, y, width, height);
          }
          break;

        case 'circle':
          if (action.startPoint && action.endPoint) {
            // Mittelpunkt = startPoint, Radius = Distanz zum endPoint
            const centerX = action.startPoint.x * exportScale;
            const centerY = action.startPoint.y * exportScale;
            const radius = Math.sqrt(
              Math.pow(action.endPoint.x - action.startPoint.x, 2) +
              Math.pow(action.endPoint.y - action.startPoint.y, 2)
            ) * exportScale;

            exportCtx.beginPath();
            exportCtx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
            exportCtx.stroke();
          }
          break;

        case 'arrow':
          if (action.startPoint && action.endPoint) {
            const headLength = 15 * exportScale;
            const startX = action.startPoint.x * exportScale;
            const startY = action.startPoint.y * exportScale;
            const endX = action.endPoint.x * exportScale;
            const endY = action.endPoint.y * exportScale;
            const dx = endX - startX;
            const dy = endY - startY;
            const angle = Math.atan2(dy, dx);

            exportCtx.beginPath();
            exportCtx.moveTo(startX, startY);
            exportCtx.lineTo(endX, endY);
            exportCtx.stroke();

            exportCtx.beginPath();
            exportCtx.moveTo(endX, endY);
            exportCtx.lineTo(
              endX - headLength * Math.cos(angle - Math.PI / 6),
              endY - headLength * Math.sin(angle - Math.PI / 6)
            );
            exportCtx.moveTo(endX, endY);
            exportCtx.lineTo(
              endX - headLength * Math.cos(angle + Math.PI / 6),
              endY - headLength * Math.sin(angle + Math.PI / 6)
            );
            exportCtx.stroke();
          }
          break;

        case 'freehand':
          if (action.points.length > 1) {
            exportCtx.beginPath();
            exportCtx.moveTo(action.points[0]!.x * exportScale, action.points[0]!.y * exportScale);
            for (let i = 1; i < action.points.length; i++) {
              exportCtx.lineTo(action.points[i]!.x * exportScale, action.points[i]!.y * exportScale);
            }
            exportCtx.stroke();
          }
          break;

        case 'eraser':
          // Radiergummi entfernt Aktionen - nichts zu exportieren
          break;
      }
    });

    // Als DataURL exportieren
    const dataUrl = exportCanvas.toDataURL('image/png');
    onSave(dataUrl);
  };

  const tools: { id: Tool; icon: React.ReactNode; label: string }[] = [
    { id: 'rectangle', icon: <Square className="w-4 h-4" />, label: 'Rechteck' },
    { id: 'circle', icon: <Circle className="w-4 h-4" />, label: 'Kreis' },
    { id: 'arrow', icon: <ArrowRight className="w-4 h-4" />, label: 'Pfeil' },
    { id: 'freehand', icon: <Pencil className="w-4 h-4" />, label: 'Freihand' },
    { id: 'eraser', icon: <Eraser className="w-4 h-4" />, label: 'Radierer' },
  ];

  const colors = [
    '#ef4444', // Rot
    '#f97316', // Orange
    '#eab308', // Gelb
    '#22c55e', // Grün
    '#3b82f6', // Blau
    '#000000', // Schwarz
  ];

  return (
    <div className="fixed inset-0 z-[10005] flex flex-col bg-gray-900">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-gray-800 border-b border-gray-700">
        <h3 className="text-sm font-medium text-white">Screenshot bearbeiten</h3>
        <div className="flex items-center gap-2">
          <button
            onClick={onCancel}
            className="px-3 py-1.5 text-sm text-gray-300 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
          >
            Abbrechen
          </button>
          <button
            onClick={handleSave}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-900 bg-brand hover:bg-brand-hover rounded-lg transition-colors"
          >
            <Check className="w-4 h-4" />
            Speichern
          </button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-4 px-4 py-2 bg-gray-800 border-b border-gray-700">
        {/* Werkzeuge */}
        <div className="flex items-center gap-1">
          {tools.map(t => (
            <button
              key={t.id}
              onClick={() => setTool(t.id)}
              className={clsx(
                'p-2 rounded-lg transition-colors',
                tool === t.id
                  ? 'bg-brand text-gray-900'
                  : 'text-gray-400 hover:text-white hover:bg-gray-700'
              )}
              title={t.label}
            >
              {t.icon}
            </button>
          ))}
        </div>

        <div className="w-px h-6 bg-gray-700" />

        {/* Farben */}
        <div className="flex items-center gap-1">
          {colors.map(c => (
            <button
              key={c}
              onClick={() => setColor(c)}
              className={clsx(
                'w-6 h-6 rounded-full border-2 transition-transform',
                color === c ? 'border-white scale-110' : 'border-transparent hover:scale-105'
              )}
              style={{ backgroundColor: c }}
              title={c}
            />
          ))}
        </div>

        <div className="w-px h-6 bg-gray-700" />

        {/* Strichstärke */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">Stärke:</span>
          <input
            type="range"
            min="1"
            max="10"
            value={lineWidth}
            onChange={(e) => setLineWidth(Number(e.target.value))}
            className="w-20 h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-brand"
          />
        </div>

        <div className="w-px h-6 bg-gray-700" />

        {/* Rückgängig */}
        <button
          onClick={handleUndo}
          disabled={actions.length === 0}
          className={clsx(
            'flex items-center gap-1.5 px-2 py-1.5 text-sm rounded-lg transition-colors',
            actions.length === 0
              ? 'text-gray-600 cursor-not-allowed'
              : 'text-gray-400 hover:text-white hover:bg-gray-700'
          )}
        >
          <Undo2 className="w-4 h-4" />
          Rückgängig
        </button>
      </div>

      {/* Canvas Container */}
      <div
        ref={containerRef}
        className="flex-1 flex items-center justify-center p-4 overflow-auto bg-gray-950"
      >
        {canvasSize.width > 0 && (
          <canvas
            ref={canvasRef}
            width={canvasSize.width}
            height={canvasSize.height}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchStart={handleMouseDown}
            onTouchMove={handleMouseMove}
            onTouchEnd={handleMouseUp}
            onTouchCancel={handleMouseUp}
            className="rounded-lg shadow-2xl cursor-crosshair touch-none"
            style={{ maxWidth: '100%', maxHeight: '100%' }}
          />
        )}
      </div>
    </div>
  );
};
